import { tr } from '@/lib/i18n-core';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AuthLineChange, EncounterInsuranceDto, PatientClaimDto, PatientPreAuthDto, PreAuthLineAnswerDto } from '@his/shared';
import { get } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { cn, dateTime, money } from '@/lib/utils';
import { DataTable } from '@/components/ui/layout';
import { Badge, Card, CardContent, CardHeader, CardTitle, StatusBadge } from '@/components/ui/primitives';
import { MessagesButton } from './MessagesDrawer';
import { changedFields, pctOf, reasonEntries } from './partial-answer';

const HIGHLIGHT = 'rounded bg-warning/20 px-1 font-semibold text-warning';

function Changed({ on, children }: { on: boolean; children: ReactNode }) {
  return <span className={cn(on && HIGHLIGHT)}>{children}</span>;
}

function qtyText(value: number | null): string {
  return value === null ? '—' : String(value);
}

export function ReasonList({ line }: { line: Pick<PreAuthLineAnswerDto, 'reasonCodes' | 'reasonText'> }) {
  const entries = reasonEntries(line.reasonCodes);
  if (entries.length === 0 && !line.reasonText) return <span className="text-muted-foreground">—</span>;
  return (
    <ul className="space-y-0.5 text-xs">
      {entries.map((entry) => (
        <li key={entry.code}>
          {entry.key ? tr('billing', entry.key) : <span className="font-mono">{entry.code}</span>}
          {entry.key && <span className="ms-1 font-mono text-muted-foreground">({entry.code})</span>}
        </li>
      ))}
      {line.reasonText && <li className="text-muted-foreground">{line.reasonText}</li>}
    </ul>
  );
}

export function PartialLinesTable({ lines }: { lines: PreAuthLineAnswerDto[] }) {
  return (
    <DataTable
      dense
      rows={lines}
      rowKey={(line) => line.lineId}
      empty={tr('billing', 'nx_no_lines')}
      columns={[
        { header: tr('billing', 'description'), cell: (line) => <span>{line.display}</span> },
        {
          header: tr('billing', 'nx_requested'),
          align: 'right',
          cell: (line) => (
            <span className="tabular">
              {line.requestedQty} · {money(line.requestedMinor)}
            </span>
          ),
        },
        {
          header: tr('billing', 'nx_approved'),
          align: 'right',
          cell: (line) => {
            const changed: Set<AuthLineChange> = changedFields(line);
            if (line.answer === 'rejected') return <Badge variant="destructive">{tr('billing', 'nx_answer_rejected')}</Badge>;
            if (line.answer === 'pended' || line.answer === 'pending') return <Badge variant="warning">{tr('billing', 'nx_answer_waiting')}</Badge>;
            return (
              <span className="tabular">
                <Changed on={changed.has('qty')}>{qtyText(line.approvedQty ?? line.requestedQty)}</Changed> · <Changed on={changed.has('amount')}>{money(line.approvedMinor)}</Changed>
              </span>
            );
          },
        },
        {
          header: tr('billing', 'nx_approved_percent'),
          align: 'right',
          cell: (line) => {
            const pct = pctOf(line);
            if (pct === null) return '—';
            return <Changed on={changedFields(line).has('pct')}>{pct}%</Changed>;
          },
        },
        { header: tr('billing', 'nx_insurer_share'), align: 'right', cell: (line) => <span className="tabular">{money(line.payerShareMinor)}</span> },
        { header: tr('billing', 'patient_share'), align: 'right', cell: (line) => <span className="tabular">{money(line.patientShareMinor)}</span> },
        { header: tr('billing', 'nx_reasons'), cell: (line) => <ReasonList line={line} /> },
      ]}
    />
  );
}

function PreAuthBlock({ preAuth }: { preAuth: PatientPreAuthDto }) {
  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium">{preAuth.preauthNo ?? tr('billing', 'nx_no_number_yet')}</span>
        <StatusBadge status={preAuth.status} />
        {preAuth.related === 'prior' && <Badge variant="info">{tr('billing', 'nx_follow_up')}</Badge>}
        <span className="text-xs text-muted-foreground">{dateTime(preAuth.requestedAt)}</span>
        <span className="ms-auto">
          <MessagesButton entityType="pre_authorization" entityId={preAuth.id} title={tr('billing', 'nx_messages_for_preauth', { number: preAuth.preauthNo ?? preAuth.id.slice(0, 8) })} />
        </span>
      </div>
      <PartialLinesTable lines={preAuth.lines} />
    </div>
  );
}

function ClaimLine({ claim }: { claim: PatientClaimDto }) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border p-2 text-sm">
      <span className="font-medium">{claim.number}</span>
      <StatusBadge status={claim.status} />
      <span className="text-xs text-muted-foreground">
        {tr('billing', 'nx_claimed')} {money(claim.claimedAmountMinor)} · {tr('billing', 'nx_approved')} {money(claim.approvedAmountMinor)}
      </span>
      <span className="ms-auto">
        <MessagesButton entityType="claim" entityId={claim.id} title={tr('billing', 'nx_messages_for_claim', { number: claim.number })} />
      </span>
    </div>
  );
}

export function useVisitInsurance(encounterId: string | null, admissionId: string | null, enabled: boolean) {
  const path = admissionId ? `/insurance/admissions/${admissionId}/insurance` : encounterId ? `/insurance/encounters/${encounterId}/insurance` : null;
  return useQuery({
    queryKey: ['visit-insurance', admissionId ?? encounterId],
    queryFn: () => get<EncounterInsuranceDto>(path as string),
    enabled: enabled && path !== null,
    staleTime: 30_000,
  });
}

export function VisitInsuranceCard({ encounterId, admissionId }: { encounterId: string | null; admissionId: string | null }) {
  const { can } = useAuth();
  const visit = useVisitInsurance(encounterId, admissionId, can('claim.read', 'preauth.manage', 'coverage.verify', 'invoice.read', 'nphies.eligibility'));
  const data = visit.data;
  if (!data || (data.preAuths.length === 0 && data.claims.length === 0)) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{tr('billing', 'nx_insurer_answers')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {data.preAuths.map((preAuth) => (
          <PreAuthBlock key={preAuth.id} preAuth={preAuth} />
        ))}
        {data.claims.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{tr('billing', 'nx_claims')}</p>
            {data.claims.map((claim) => (
              <ClaimLine key={claim.id} claim={claim} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
